import { Seo } from '../components/Seo'
import { PageHeader } from '../components/common/PageHeader'
import { company } from '../data/company'
import { breadcrumbSchema } from '../lib/seo'

type Doc = 'privacy' | 'terms' | 'disclaimer'

interface LegalSection {
  heading: string
  body: string[]
}
interface LegalContent {
  path: string
  label: string
  title: string
  intro: string
  sections: LegalSection[]
}

const LAST_UPDATED = 'August 2026'

const CONTENT: Record<Doc, LegalContent> = {
  privacy: {
    path: '/privacy-policy',
    label: 'Privacy Policy',
    title: 'Privacy Policy',
    intro:
      'This policy explains how GreenbayBio handles your information — both on our public website and in the Greenbay mobile app used by our staff, retailers, distributors and farmers.',
    sections: [
      {
        heading: 'Who this policy covers',
        body: [
          'This policy applies to the public GreenbayBio website and to the Greenbay mobile app (the “App”). The App is a business tool for Greenbay Biotech’s authorised field staff, retailers, distributors and farmers, so much of the information it handles is tied to your role and your work with us.',
        ],
      },
      {
        heading: 'Information we collect',
        body: [
          'Account and contact details: your name and phone number, and optionally your email. Your phone number is used to sign you in — we send a one-time password (OTP) to it over WhatsApp. We do not use passwords.',
          'Location: for field staff using the App on duty, we collect precise device location, including in the background while you are checked in, to record attendance, verify field visits, build route history, and detect fraud (for example, mock-location spoofing). For farmers and retailers, we may capture your approximate location when you submit an enquiry or place an order.',
          'Photos: field staff attendance check-in/check-out selfies, and geo-tagged photos attached to visits and expense claims.',
          'Device information: device model, app version, IP address, and a push-notification token (Google Firebase Cloud Messaging) used to deliver notifications to your device.',
          'Business records: the orders, invoices, payments, ledgers, visits, stock movements and related transactional data created as you use the App in your role.',
          'Website enquiries: when you submit an enquiry on our website we ask for your name and phone number, and optionally your email, location, the product you’re interested in, and your message.',
        ],
      },
      {
        heading: 'How we use your information',
        body: [
          'To sign you in and secure your account; to deliver notifications you have opted into; to record attendance, field visits and route history for staff; to process orders, invoices, payments and other business operations; to prevent and detect fraud; and to provide support and respond to your enquiries.',
          'Location and camera data are used only for the field-force and business purposes described above, and are collected while you are using the App in your role.',
        ],
      },
      {
        heading: 'How we share your information',
        body: [
          'We do not sell your personal information. We share it only with service providers who help us run the App, and only as needed: Meta/WhatsApp (to deliver your login OTP), Google Firebase (to deliver push notifications), and map/tile providers (to show maps). We may also disclose information where required by law.',
        ],
      },
      {
        heading: 'Data retention',
        body: [
          'We keep your information for as long as your account is active and as needed to provide the service. Financial and tax records — such as invoices, payments and ledgers — are retained for as long as Indian law requires (for example, under GST rules, up to eight years), even after an account is closed.',
          'Other personal data, such as location traces, attendance selfies and visit photos, is retained only for the operational period needed for attendance, verification and audit, and is then removed.',
        ],
      },
      {
        heading: 'Your choices and account deletion',
        body: [
          'You can request deletion of your account and associated personal data at any time. In the App, this can be done from your profile; on the web, you can request it at greenbaybio.com/delete-account without installing the App.',
          'When you delete your account, we remove your login, anonymise your personal details (such as name, phone number and photos), and stop location tracking and notifications. We retain only the records we are legally required to keep (such as tax and invoice records), in anonymised form where possible.',
          'You can also turn off location and notifications at any time in your device settings.',
        ],
      },
      {
        heading: 'Security',
        body: [
          'Login tokens are stored encrypted on your device, and access to business data is controlled by your role and permissions. We take reasonable measures to protect your information, though no system can be guaranteed completely secure.',
        ],
      },
      {
        heading: 'Contact',
        body: [`For any privacy question, or to request account deletion, contact us at ${company.email} or ${company.phoneDisplay}.`],
      },
    ],
  },
  terms: {
    path: '/terms',
    label: 'Terms of Use',
    title: 'Terms of Use',
    intro: 'These terms cover both the GreenbayBio website and the Greenbay mobile app and account. By using them, you agree to the following.',
    sections: [
      {
        heading: 'Use of this website',
        body: [
          'This site is provided for general information about GreenbayBio and its products. You may browse and share it for lawful, non-commercial purposes.',
          'All content, branding and imagery on this site belong to their respective owners and may not be reproduced without permission.',
        ],
      },
      {
        heading: 'The Greenbay app and your account',
        body: [
          'The Greenbay mobile app is a business tool for Greenbay Biotech’s authorised staff, retailers, distributors and farmers. Accounts are created and accessed using your phone number and a one-time password (OTP); there is no password.',
          'You are responsible for activity on your account, for keeping your device secure, and for providing accurate information. Different roles — farmer, retailer and staff — see different features and data, and you may access only what your role authorises.',
        ],
      },
      {
        heading: 'Acceptable use',
        body: [
          'Do not share your account or OTP, attempt to access data or features you are not authorised for, scrape or reverse-engineer the app, or misuse pricing, customer, financial or personal data you can see in your role.',
        ],
      },
      {
        heading: 'Product information',
        body: [
          'We aim to keep product information accurate and up to date, drawn from our official product documentation. Always follow the instructions, dosage and precautions printed on the product label.',
          'Product availability, pack sizes and approvals may change over time.',
        ],
      },
      {
        heading: 'No warranty of results',
        body: [
          'Information on this site and in the app is provided “as is”, without any warranty as to specific outcomes. Crop results depend on many factors outside our control.',
        ],
      },
      {
        heading: 'Account suspension and deletion',
        body: [
          'We may suspend or deactivate an account that is misused or that breaches these terms.',
          'You may request deletion of your account and associated personal data at any time — from your profile in the app, or on the web at greenbaybio.com/delete-account. On deletion we remove your login and anonymise your personal details, retaining only records we are legally required to keep (such as tax and invoice records).',
        ],
      },
      {
        heading: 'Governing law',
        body: ['These terms are governed by the laws of India, and any disputes are subject to the jurisdiction of the courts of Kolkata.'],
      },
    ],
  },
  disclaimer: {
    path: '/disclaimer',
    label: 'Disclaimer',
    title: 'Disclaimer',
    intro: 'Please read this disclaimer before relying on any information on this website.',
    sections: [
      {
        heading: 'General information only',
        body: [
          'The agronomic guidance, crop recommendations and dosage summaries on this website are general information to help you get started. They are not a substitute for advice tailored to your specific field, soil and conditions.',
          'Always follow the exact dosage, timing and precautions on the product label, and consult a Greenbay agronomic advisor or a qualified agronomist for your situation.',
        ],
      },
      {
        heading: 'No guarantee of results',
        body: [
          'We make no guarantee of any particular yield, income or outcome. Product performance depends on soil condition, climate, crop management and other factors beyond our control.',
        ],
      },
      {
        heading: 'Approvals & certifications',
        body: [
          'Approvals and certifications (such as NPOP, FCO, EU/NOP and USDA BioPreferred®) apply to specific products as stated on each product page, and may vary by product and region.',
        ],
      },
      {
        heading: 'Imagery',
        body: [
          'Some gallery imagery on this website is represented by branded placeholders and will be replaced with actual photography.',
        ],
      },
    ],
  },
}

export default function LegalPage({ doc }: { doc: Doc }) {
  const c = CONTENT[doc]
  return (
    <>
      <Seo
        title={`${c.label} | GreenbayBio`}
        description={c.intro}
        path={c.path}
        jsonLd={[breadcrumbSchema([{ name: 'Home', path: '/' }, { name: c.label, path: c.path }])]}
      />
      <PageHeader eyebrow="Legal" title={c.title} subtitle={c.intro} />

      <div className="container-x max-w-prose py-14">
        <p className="mb-8 text-sm text-ink-500">Last updated: {LAST_UPDATED}</p>
        <div className="space-y-10">
          {c.sections.map((s) => (
            <section key={s.heading}>
              <h2 className="font-display text-h3 font-semibold text-ink">{s.heading}</h2>
              <div className="mt-3 space-y-3 leading-relaxed text-ink-600">
                {s.body.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>
    </>
  )
}
